import { Injectable } from '@angular/core';
import {
  AngularFireStorage,
  AngularFireStorageReference,
  AngularFireUploadTask,
} from '@angular/fire/storage';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor(private readonly storage: AngularFireStorage) { }

  uploadFileAndGetMetadata(
    mediaFolderPath: string,
    fileToUpload: File,
    forcedName?: string
  ): FilesUploadMetadata {
    const filename = forcedName ? forcedName : fileToUpload.name;
    const filePath = `${mediaFolderPath}/${filename}`;
    const uploadTask: AngularFireUploadTask = this.storage.upload(
      filePath,
      fileToUpload,
    );
    return {
      uploadProgress$: uploadTask.percentageChanges(),
      downloadUrl$: this.getDownloadUrl$(uploadTask, filePath),
    };
  }

  public getDownloadUrl(path: string) {
    return this.storage.ref(path).getDownloadURL();
  }

  public deleteFileByUrl(url: string) {
    return this.deleteFile(this.storage.refFromURL(url));
  }

  public deleteFileByPath(path: string) {
    return this.deleteFile(this.storage.ref(path));
  }

  public fileExistsByUri(url: string) {
    return this.fileExists(this.storage.refFromURL(url));
  }

  public fileExistsByPath(path: string) {
    return this.fileExists(this.storage.ref(path));
  }

  private deleteFile(ref: AngularFireStorageReference) {
    return this.fileExists(ref).pipe(
      switchMap(exists => {
        if (exists) {
          return ref.delete().pipe(
            catchError(error => {
              console.error(error);
              throw(error);
            })
          );
        } else {
          return of(null);
        }
      })
    )


  }

  private fileExists(ref: AngularFireStorageReference) {
    return ref.getDownloadURL().pipe(
      map(() => true),
      catchError(error => {
        if (error.code !== 'storage/object-not-found') {
          console.error(error);
        }
        return of(false);
      })
    )
  }

  private getDownloadUrl$(
    uploadTask: AngularFireUploadTask,
    path: string,
  ): Observable<string> {
    return from(uploadTask).pipe(
      switchMap((_) => this.storage.ref(path).getDownloadURL()),
    );
  }
}

export interface FilesUploadMetadata {
  uploadProgress$: Observable<number>;
  downloadUrl$: Observable<string>;
}
